import type { BadgeTone } from '@/components/ui/Badge';

/**
 * What a score is worth, said in words.
 *
 * A number out of twenty tells a teacher something; it tells a child and a
 * parent very little. Each band carries the grade an institute would write on a
 * report and a line of praise or direction the teacher can send as it stands —
 * which is the difference between a mark and feedback.
 *
 * Lives here rather than in one feature's folder because three screens already
 * grade against it — class activities, exam marks, and the report a parent will
 * eventually read — and a second copy would be the thing that disagrees the
 * first time a band moves (CLAUDE.md §3).
 *
 * Bands are read top down and the first one the percentage clears wins, so
 * `floor` is the *minimum* for that grade. 33 is the bottom because that is the
 * pass mark this product defaults to (`InstituteSettings.passingPercentage`).
 *
 * Deliberately not read from the institute's own settings on the client: the exam
 * endpoints already resolve a paper's real pass mark server-side and send it
 * down, so these bands describe the *shape* of a grade scale rather than
 * claiming to be any one institute's rulebook.
 */

export interface GradeBand {
  /** Lowest percentage that earns this grade. */
  floor: number;
  /** What a report card would show. */
  grade: string;
  label: string;
  /** Ready to send as feedback, in the teacher's voice. */
  compliment: string;
  tone: BadgeTone;
}

export const GRADE_BANDS: readonly GradeBand[] = [
  {
    floor: 90,
    grade: 'A+',
    label: 'Outstanding',
    compliment: 'Outstanding work — carefully made and beautifully presented. Well done.',
    tone: 'success',
  },
  {
    floor: 75,
    grade: 'A',
    label: 'Excellent',
    compliment: 'Excellent effort. The work is clear and thorough — keep it up.',
    tone: 'success',
  },
  {
    floor: 60,
    grade: 'B',
    label: 'Good',
    compliment: 'Good work. A little more detail in the explanation would lift it further.',
    tone: 'info',
  },
  {
    floor: 45,
    grade: 'C',
    label: 'Fair',
    compliment: 'A fair attempt. Go over the weaker parts again and you will see the difference.',
    tone: 'warning',
  },
  {
    floor: 33,
    grade: 'D',
    label: 'Needs work',
    compliment: 'This needs more work. Bring it to me and we will go through it together.',
    tone: 'warning',
  },
  {
    floor: 0,
    grade: 'E',
    label: 'Below expectation',
    compliment: 'Not there yet. Come and see me so we can make a plan for the next one.',
    tone: 'critical',
  },
];

/**
 * What a score works out to as a grade, or null when there is nothing to grade.
 *
 * Null for an unscored piece of work and for anything not yet marked — the
 * caller renders nothing in both cases, which is what keeps "no grade yet" and
 * "a poor grade" from looking alike.
 *
 * Takes the score as it is typed, so a banner can follow the box while somebody
 * is still deciding: a half-typed "1" of an intended "18" is a real score of 1
 * until the second digit lands, and showing it is honest.
 */
export function gradeFor(score: number | null, outOf: number | null): GradeBand | null {
  if (score === null || outOf === null || outOf <= 0) return null;
  if (!Number.isFinite(score) || score < 0) return null;

  const percent = (score / outOf) * 100;
  // First band the percentage clears wins; the list runs highest first.
  return GRADE_BANDS.find((band) => percent >= band.floor) ?? null;
}

/**
 * `gradeFor`, held to the paper's own pass mark.
 *
 * The bands assume a pass at 33%, but a paper can set its own (40 out of 100,
 * 10 out of 50). Left alone, a failed 35/100 would read "D" and a passed 12/50
 * would read "E" — the badge and the pass/fail verdict beside it disagreeing.
 * The scale stays the one source; the pass mark only decides which side of the
 * line a score may land: below it can never wear a passing band, at or above it
 * never the failing one.
 */
export function gradeForPaper(
  score: number | null,
  outOf: number | null,
  passMarks: number,
): GradeBand | null {
  const band = gradeFor(score, outOf);
  if (band === null || score === null) return null;

  const failingBand = GRADE_BANDS[GRADE_BANDS.length - 1];
  const lowestPassingBand = GRADE_BANDS[GRADE_BANDS.length - 2];
  if (failingBand === undefined || lowestPassingBand === undefined) return band;

  const passed = score >= passMarks;
  if (!passed) return failingBand;
  return band === failingBand ? lowestPassingBand : band;
}

/** Reads what is in a score box, or null when it is empty or not a number. */
export function parseScore(value: string): number | null {
  const trimmed = value.trim();
  if (trimmed === '') return null;

  const parsed = Number(trimmed);
  return Number.isFinite(parsed) ? parsed : null;
}

/**
 * How a band is drawn, from its tone alone.
 *
 * The exam marks report, the family results screens and the profile all colour
 * a grade; each used to carry its own map, and two of them painted every grade
 * green — a failing "E" wearing the colour of an "A+". One map here, so a grade
 * reads the same wherever it is shown and in both themes (status tokens only).
 */
export const GRADE_TONE_CLASSES: Record<
  BadgeTone,
  { text: string; dot: string; badge: string }
> = {
  success: { text: 'text-ok', dot: 'bg-ok', badge: 'bg-ok-bg text-ok border-ok' },
  info: { text: 'text-info', dot: 'bg-info', badge: 'bg-info-bg text-info border-info' },
  warning: { text: 'text-warn', dot: 'bg-warn', badge: 'bg-warn-bg text-warn border-warn' },
  critical: { text: 'text-crit', dot: 'bg-crit', badge: 'bg-crit-bg text-crit border-crit' },
  neutral: { text: 'text-ink-2', dot: 'bg-ink-3', badge: 'bg-surface-2 text-ink-2 border-line' },
  accent: {
    text: 'text-accent',
    dot: 'bg-accent',
    badge: 'bg-accent-soft text-accent border-accent-line',
  },
};

/** The classes for a band, or the neutral ones beside a dash when there is no grade. */
export function gradeToneClasses(band: GradeBand | null): {
  text: string;
  dot: string;
  badge: string;
} {
  return GRADE_TONE_CLASSES[band?.tone ?? 'neutral'];
}
